import classNames from "classnames";
import React, { useEffect, useState, useCallback } from "react";
import Button from "../../button/Button";
import Input from "../../input/Input";
import SelectItem from "../../selectItem/SelectItem";
import BaseModal from "../baseModal/BaseModal";
import styles from "./UpdateJarVersionModal.module.scss";
import moment from "moment";
import appVersionsService from "../../../apis/services/admin/appVersionsService";

const UpdateJarVersionModal = ({
  modalOpen,
  onClose,
  selectedFiscalDevices,
  onRemoveDeviceFromList,
  onSubmit,
}) => {
  const [singleDate, setSingleDate] = useState(null);
  const [jarFileUrl, setJarFileUrl] = useState("");
  const [urlSource, setUrlSource] = useState("version"); // "version" or "custom"
  const [appVersions, setAppVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Fetch app versions when modal opens
  const fetchVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const response = await appVersionsService.fetchVersions(false);
      const versions = Array.isArray(response) ? response : response?.data || [];
      setAppVersions(versions);
      // Auto-select latest version if available
      const latestVersion = versions.find(v => v.isLatest);
      if (latestVersion) {
        setSelectedVersionId(latestVersion.id.toString());
        setJarFileUrl(latestVersion.otaUrl);
      }
    } catch (err) {
      console.error("Error fetching app versions:", err);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen) {
      fetchVersions();
    }
  }, [modalOpen, fetchVersions]);

  useEffect(() => {
    if (selectedFiscalDevices.length === 0) onClose();
  }, [selectedFiscalDevices, onClose]);

  // Handle version selection
  const handleVersionChange = (e) => {
    const versionId = e.target.value;
    setSelectedVersionId(versionId);
    if (versionId) {
      const version = appVersions.find(v => v.id.toString() === versionId);
      if (version) {
        setJarFileUrl(version.otaUrl);
      }
    } else {
      setJarFileUrl("");
    }
  };

  // Handle URL source change
  const handleUrlSourceChange = (source) => {
    setUrlSource(source);
    if (source === "version") {
      // Reset to selected version URL
      if (selectedVersionId) {
        const version = appVersions.find(v => v.id.toString() === selectedVersionId);
        if (version) {
          setJarFileUrl(version.otaUrl);
        }
      }
    } else {
      // Clear for custom input
      setJarFileUrl("");
      setSelectedVersionId("");
    }
  };

  const renderSelectedFiscalDevices = () => {
    return selectedFiscalDevices.map((device) => (
      <div className={styles.itemWrap} key={device.id}>
        <SelectItem
          title={device.name}
          onDelete={() => {
            onRemoveDeviceFromList(device);
          }}
        />
      </div>
    ));
  };

  return (
    <BaseModal modalOpen={modalOpen} onClose={onClose} title="OTA Update">
      <div className={styles.content}>
        <div>
          <div className={styles.listWrap}>{renderSelectedFiscalDevices()}</div>
          
          <div className={classNames(styles.inputWrapper, styles.marginTop)}>
            <p>Datum</p>
            <input
              type="datetime-local"
              id="startDate"
              name="startDate"
              className={styles.dateInput}
              onChange={(e) => setSingleDate(e.target.value)}
            />
          </div>

          {/* URL Source Toggle */}
          <div className={classNames(styles.inputWrapper, styles.marginTop)}>
            <p>Izvor verzije</p>
            <div className={styles.urlSourceToggle}>
              <button
                type="button"
                className={classNames(
                  styles.toggleButton,
                  urlSource === "version" && styles.toggleButtonActive
                )}
                onClick={() => handleUrlSourceChange("version")}
              >
                📦 Registrovana verzija
              </button>
              <button
                type="button"
                className={classNames(
                  styles.toggleButton,
                  urlSource === "custom" && styles.toggleButtonActive
                )}
                onClick={() => handleUrlSourceChange("custom")}
              >
                ✏️ Custom URL
              </button>
            </div>
          </div>

          {/* Version Selector */}
          {urlSource === "version" && (
            <div className={classNames(styles.inputWrapper, styles.marginTop)}>
              <p>Izaberi verziju</p>
              {loadingVersions ? (
                <p className={styles.loadingText}>Učitavanje verzija...</p>
              ) : (
                <select
                  className={styles.versionSelect}
                  value={selectedVersionId}
                  onChange={handleVersionChange}
                >
                  <option value="">-- Izaberi verziju --</option>
                  {appVersions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.version}
                      {version.isLatest ? " ⭐ (Latest)" : ""}
                      {!version.isActive ? " (Neaktivna)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Custom URL Input */}
          {urlSource === "custom" && (
            <div className={classNames(styles.inputWrapper, styles.marginTop)}>
              <p>Custom URL</p>
              <Input 
                name="link" 
                value={jarFileUrl} 
                onChange={setJarFileUrl}
                placeholder="https://..."
              />
            </div>
          )}

          {/* Show selected URL */}
          {jarFileUrl && (
            <div className={classNames(styles.inputWrapper, styles.marginTop)}>
              <p>OTA URL</p>
              <div className={styles.selectedUrl}>
                <a href={jarFileUrl} target="_blank" rel="noopener noreferrer">
                  {jarFileUrl.length > 60 ? jarFileUrl.substring(0, 60) + "..." : jarFileUrl}
                </a>
              </div>
            </div>
          )}
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            name={"Sačuvaj"}
            onClick={() => {
              onSubmit({
                jarFileUrl,
                fiscalDeviceIds: selectedFiscalDevices.map(
                  (device) => device.id
                ),
                date: moment(new Date(singleDate))
                  .tz("Europe/Belgrade")
                  .format("DD-MM-YYYY HH:mm:ss"),
              });
            }}
            disabled={!jarFileUrl || !singleDate}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default UpdateJarVersionModal;
